import { create } from 'zustand';
import { Notification } from '@/types/notification';
import { notificationService } from '@/services/notification.service';
import {
  subscribeToNotifications,
  unsubscribeNotifications,
} from '@/lib/realtime-notifications';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { useAuthStore } from './auth.store';

interface NotificationsState {
  notifications: Notification[];
  unreadCount: number;
  loading: boolean;
  error: string | null;

  // Actions
  fetchNotifications: () => Promise<void>;
  fetchUnreadCount: () => Promise<number>;
  markAsRead: (id: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  removeNotification: (id: string) => Promise<void>;
  addNotification: (notification: Notification) => void;
  clearError: () => void;
  initializeRealtime: () => void;
  cleanupRealtime: () => void;
}

let realtimeChannel: RealtimeChannel | null = null;

export const useNotificationsStore = create<NotificationsState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  loading: false,
  error: null,

  initializeRealtime: () => {
    if (typeof window === 'undefined') return;
    const { user } = useAuthStore.getState();
    if (!user) return;

    // Limpar subscrição anterior se existir
    unsubscribeNotifications(realtimeChannel);
    realtimeChannel = null;

    realtimeChannel = subscribeToNotifications(
      (notification) => {
        set((state) => ({
          notifications: [notification, ...state.notifications],
          unreadCount: notification.read ? state.unreadCount : state.unreadCount + 1,
        }));
      },
      () => {
        get().fetchUnreadCount();
      }
    );
  },

  cleanupRealtime: () => {
    unsubscribeNotifications(realtimeChannel);
    realtimeChannel = null;
  },

  fetchNotifications: async () => {
    set({ loading: true, error: null });
    try {
      const notifications = await notificationService.findAll();
      const unreadCount = notifications.filter(n => !n.read).length;
      set({ notifications, unreadCount, loading: false });
    } catch (err: any) {
      set({ 
        error: err.response?.data?.message || 'Erro ao carregar notificações',
        loading: false 
      });
    }
  },

  fetchUnreadCount: async () => {
    try {
      const count = await notificationService.getUnreadCount();
      set({ unreadCount: count });
      return count;
    } catch (err: any) {
      console.error('Erro ao buscar contador de não lidas:', err);
      return 0;
    }
  },

  markAsRead: async (id: string) => {
    try {
      await notificationService.markAsRead(id);
      set((state) => ({
        notifications: state.notifications.map(n => 
          n.id === id ? { ...n, read: true } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1)
      }));
    } catch (err: any) {
      set({ 
        error: err.response?.data?.message || 'Erro ao marcar notificação como lida'
      });
    }
  },

  markAllAsRead: async () => {
    try {
      await notificationService.markAllAsRead();
      set((state) => ({
        notifications: state.notifications.map(n => ({ ...n, read: true })),
        unreadCount: 0
      }));
    } catch (err: any) {
      set({ 
        error: err.response?.data?.message || 'Erro ao marcar todas como lidas'
      });
    }
  },

  removeNotification: async (id: string) => {
    try {
      await notificationService.remove(id);
      set((state) => {
        const n = state.notifications.find(x => x.id === id);
        return {
          notifications: state.notifications.filter(x => x.id !== id),
          unreadCount: n && !n.read ? Math.max(0, state.unreadCount - 1) : state.unreadCount
        };
      });
    } catch (err: any) {
      set({ 
        error: err.response?.data?.message || 'Erro ao excluir notificação'
      });
    }
  },

  addNotification: (notification: Notification) => {
    set((state) => ({
      notifications: [notification, ...state.notifications],
      unreadCount: notification.read ? state.unreadCount : state.unreadCount + 1
    }));
  },

  clearError: () => {
    set({ error: null });
  },
}));
